import { useState, type ReactNode } from 'react';
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import { Button, FilterResetButton, cn } from '../ui';

/* ---------------------------------------------------------------------------
 * FILTERS, for a list with more than a handful of them.
 *
 * An inline toolbar works up to about four controls. Past that it wraps onto three or four lines and
 * gives every filter equal weight; a min/max pair that is really ONE filter reads as two.
 *
 * The answer is NOT a dropdown, though. A 560px popover over the table put the controls in a cramped
 * column with its own scrollbar, hid the rows being filtered, and floated a button over the data.
 * Filtering is something you do WHILE looking at the result.
 *
 * So the panel opens IN PLACE, under the toolbar and above the rows: full page width, sections laid
 * out side by side, nothing overlapped, nothing scrolling inside anything else. Filters apply as you
 * change them — there is no "Apply", because the list under your eyes is already the answer.
 * ------------------------------------------------------------------------- */

export interface FilterField {
  /** Field label, shown above the control. */
  label: ReactNode;
  control: ReactNode;
  /** Cells to occupy. A min/max pair or a date range needs 2; a single select needs 1. */
  span?: 1 | 2;
  /** A line under the control for anything the label cannot carry. Use it sparingly — every hint
   *  costs a row of height across the whole panel. */
  hint?: ReactNode;
}

export interface FilterGroup {
  /** Section heading — name it after the COLUMN the filters act on. */
  title: string;
  fields: FilterField[];
  /**
   * OPTIONAL identity hue for this section — any CSS colour, usually one from `lib/palette`.
   *
   * A panel of five sections separated only by hairlines is five grey boxes: the eye has to read
   * every heading to find the one it wants, every time. Given a hue the section gets a tinted wash,
   * a coloured rule and a coloured heading, so "the blue one is the route" survives being looked
   * away from. Purely an aid to FINDING a section — it carries no status meaning, which is why the
   * ramp to draw from is `IDENTITY_SERIES` (no red, no brown).
   *
   * Left off, the section keeps the plain hairline-and-grey heading, so the pages that were built
   * before this are unchanged.
   */
  tone?: string;
  /** Small glyph beside the heading, in the section's tone. Only meaningful alongside `tone`. */
  icon?: ReactNode;
}

/**
 * A FILTER OPTION THAT CARRIES THE COLOUR OF THE THING IT PICKS.
 *
 * A panel of eight dropdowns of identical grey text means finding the one that says "Collection"
 * is a word-by-word read, and a CLOSED control says nothing at all about what it is set to. Each
 * option wears a dot in the hue that state already has on the page it filters — the same green /
 * gold / blue its tag wears in the table and its tile wears in the band — so a filter and the rows
 * it produces are one colour.
 *
 * A DOT and not a full Tag: a tag inside a select's trigger reads as a chip you can remove, and a
 * panel of them shouts louder than the table being filtered. The dot rides into the closed trigger,
 * which is where it earns the most.
 *
 * LOOK THE HUE UP, NEVER RE-PICK IT — pass what the page's own status→tone map resolves to (through
 * `TAG_TONE_HUE` in `lib/palette`). A status that is gold on the filter and blue in the rows is a
 * bug that one definition makes impossible.
 *
 * NO HUE = the option narrows nothing ("Any", "All records") or the state is deliberately colourless
 * — drawn as a hollow ring, which keeps the text of every option on one left edge instead of leaving
 * the uncoloured ones indented differently.
 *
 * `title` is the plain text: the option is SEARCHED by it once the label is a node, it is what the
 * trigger shows on hover when a narrow filter cell clips a long option, and it is the string a page
 * builds an applied-filter chip from (a node in a template literal prints "[object Object]").
 */
export const hueOption = (value: string, text: string, hue?: string, count?: number) => ({
  value,
  title: text,
  // Rendered as a badge by `renderFacetOption`; kept off the label so the closed control reads clean.
  ...(count != null ? { count } : {}),
  label: (
    <span className="flex min-w-0 items-center gap-2">
      <span
        aria-hidden
        className={cn('size-2 shrink-0 rounded-full', !hue && 'border-[1.5px] border-line-strong')}
        style={hue ? { background: hue } : undefined}
      />
      <span className="min-w-0 truncate">{text}</span>
    </span>
  ),
});

/**
 * A pair of controls that is really ONE filter — a min/max band, a date range.
 *
 * Two separate boxes with a dash floating between them read as two unrelated filters, which is what
 * they looked like. Here they share a border and only the outer corners are rounded, so the pair is
 * a single segmented control. The overrides reach INTO the children because the kit's number input
 * carries its border on its own root while the date picker carries it on the button inside — one
 * selector could not cover both.
 */
export function FilterRange({ from, to }: { from: ReactNode; to: ReactNode }) {
  return (
    <div
      className={[
        'flex items-stretch',
        '[&>*]:min-w-0 [&>*]:flex-1',
        '[&>*:first-child]:rounded-r-none! [&>*:first-child_button]:rounded-r-none!',
        '[&>*:last-child]:rounded-l-none! [&>*:last-child_button]:rounded-l-none!',
        // Collapse the shared edge so the seam is one line, not two.
        '[&>*:last-child]:-ml-px',
        // The focused half sits above its neighbour so its ring is never clipped by the seam.
        '[&>*:focus-within]:relative [&>*:focus-within]:z-10',
        // A date button insists on 9rem of its own; inside a shared shell it must yield to the flex.
        '[&_button]:min-w-0!',
      ].join(' ')}
    >
      {from}
      {to}
    </div>
  );
}

/** One applied filter, shown as a removable chip in the toolbar. */
export interface FilterChip {
  key: string;
  label: ReactNode;
  onRemove: () => void;
}

/**
 * Returns the three pieces a page renders: the toolbar `trigger`, the `chips` beside it, and the
 * `panel` for the table's `toolbarBelow` slot.
 *
 * Split into three because they belong in two different places — the button and the chips sit in the
 * toolbar row, the panel opens on its own row underneath.
 */
export function useFilterPanel({ groups, chips, onClear, label = 'Filters' }: {
  groups: FilterGroup[];
  chips: FilterChip[];
  onClear: () => void;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const n = chips.length;

  const trigger = (
    <Button
      size="sm"
      variant={n ? 'primary' : 'default'}
      aria-expanded={open}
      onClick={() => setOpen((o) => !o)}
      icon={<SlidersHorizontal size={16} />}
    >
      {label}{n > 0 ? ` · ${n}` : ''}
      <ChevronDown size={14} className={`ml-0.5 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
    </Button>
  );

  /* What is applied stays in the TOOLBAR, whether the panel is open or shut — you should never have
     to open the filters to find out what is narrowing the list. */
  const chipRow = n > 0 ? (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      {chips.map((c) => (
        <span
          key={c.key}
          className="inline-flex max-w-full items-center gap-1 rounded-pill border border-brand-500/30 bg-brand-500/8 py-0.5 pl-2.5 pr-1 type-caption text-brand-700 dark:text-brand-300"
        >
          <span className="truncate">{c.label}</span>
          <button
            type="button"
            aria-label={`Remove filter: ${typeof c.label === 'string' ? c.label : c.key}`}
            onClick={c.onRemove}
            className="grid size-4 shrink-0 place-items-center rounded-full text-brand-700/70 transition-colors hover:bg-brand-500/15 hover:text-brand-700 dark:text-brand-300/70 dark:hover:text-brand-300"
          >
            <X size={12} />
          </button>
        </span>
      ))}
      {n > 1 && (
        <button
          type="button"
          onClick={onClear}
          className="type-caption text-muted underline-offset-2 transition-colors hover:text-content hover:underline"
        >
          Clear all
        </button>
      )}
    </div>
  ) : null;

  const panel = open ? (
    <section
      aria-label={label}
      className="mb-3 animate-expand-down rounded-card border border-line bg-surface-2/40 px-4 py-3"
    >
      {/*
        * Groups flow beside each other AND GROW to fill the row.
        *
        * Three earlier layouts wasted the panel. A column per section gave a "Tours ≥" box the same
        * 420px as a date range. A grid with full-width headings forced each two-field section onto
        * its own row. Fixed-width groups then left a third of a 1700px screen empty to their right.
        *
        * Now a group's flex BASIS is proportional to how many cells it holds, so a five-field
        * section claims more room than a two-field one, and every group on a row stretches to
        * consume whatever is left over. Nothing is stranded to the right of anything.
        */}
      <div className="flex flex-wrap items-start gap-x-6 gap-y-4">
        {groups.map((g) => {
          const cells = g.fields.reduce((sum, f) => sum + (f.span ?? 1), 0);
          return (
            <div
              key={g.title}
              className="min-w-0"
              /* NO BACKGROUND WASH ON A GROUP.
                 Each toned section used to sit on a 5% tint of its own hue. Four of them side by side
                 turned one panel into four coloured cards, every input a box on a card — and the tints
                 are what the eye lands on first, ahead of the filters themselves. The hue now lives
                 only where it identifies the section: the heading, its glyph and the rule under it.
                 A toned group therefore lays out exactly like an untoned one. */
              style={{ flex: `${cells} 1 ${cells * 9.5}rem` }}
            >
              <h4
                className={`mb-1.5 flex items-center gap-1.5 border-b pb-1 type-overline ${g.tone ? '' : 'border-line/70 text-subtle'}`}
                style={g.tone
                  ? { borderColor: `color-mix(in srgb, ${g.tone} 35%, transparent)`, color: g.tone }
                  : undefined}
              >
                {g.tone && g.icon ? <span className="grid shrink-0 place-items-center">{g.icon}</span> : null}
                <span className="min-w-0 truncate">{g.title}</span>
              </h4>
              <div className="flex flex-wrap items-start gap-x-3 gap-y-2.5">
                {g.fields.map((f, i) => (
                  <div
                    key={i}
                    className="min-w-0"
                    /* A pair (min/max, a date range) needs room for TWO controls: it used to be
                       capped at 17rem, so its second field overflowed under the next label. */
                    style={{ flex: `${f.span ?? 1} 1 ${(f.span ?? 1) * 9}rem` }}
                  >
                    {/* The control is WRAPPED by its label, so the two are associated without having
                        to mint an id for every field — the label reads out with the control, and
                        clicking the text focuses it. */}
                    <label className="block">
                      <span className="mb-1 block truncate type-label text-muted">{f.label}</span>
                      {/* Every control fills its cell — uniform width is what stops a filter form
                          from looking like a ransom note of differently sized boxes. */}
                      <span className="block [&_>*]:w-full">{f.control}</span>
                    </label>
                    {f.hint && <div className="mt-1 type-caption leading-snug text-subtle">{f.hint}</div>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-line/70 pt-2.5">
        <span className="type-caption text-subtle">
          {n === 0 ? 'No filters applied' : `${n} filter${n === 1 ? '' : 's'} applied`}
          {' · '}the list updates as you change them
        </span>
        <span className="flex items-center gap-2">
          <FilterResetButton onClick={onClear} count={n} disabled={n === 0} />
          <Button size="sm" onClick={() => setOpen(false)}>Close</Button>
        </span>
      </div>
    </section>
  ) : null;

  return { trigger, chips: chipRow, panel, open, setOpen };
}
